import React, { useRef } from 'react';
import { useProfile } from '@/features/profile/hooks/useProfile';
import PageBreadcrumb from '@/components/common/PageBreadCrumb';
import PageMeta from '@/components/common/PageMeta';
import Button from '@/components/ui/button/Button';
import { Modal } from '@/components/ui/modal';
import { useModal } from '@/shared/hooks/useModal';
import EditProfileForm from '@/features/profile/components/EditProfileForm';
import { useQueryClient } from '@tanstack/react-query';
import { Loader } from '@/components/ui/loader/Loader';
import { useTranslation } from 'react-i18next';

interface ProfileFieldProps {
  label: string;
  value: React.ReactNode;
}

const ProfileField: React.FC<ProfileFieldProps> = ({ label, value }) => (
  <div className="flex flex-col gap-1/2">
    <span className="block text-sm font-normal text-text-tertiary">{label}</span>
    <span className="block text-lg text-text-primary">{value}</span>
  </div>
);

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { isLoading, userProfile, employee, role } = useProfile();
  const { isOpen: isEditOpen, openModal: openEditModal, closeModal: originalCloseEditModal } = useModal();
  const formRef = useRef<{ handleClose: () => void; isDirty: () => boolean } | null>(null);
  const queryClient = useQueryClient();

  const closeEditModal = () => {
    if (formRef.current?.isDirty?.()) {
      formRef.current.handleClose();
    } else {
      originalCloseEditModal();
    }
  };

  if (isLoading) {
    return <Loader className='h-full w-full mt-60' />;
  }

  return (
    <>
      <PageMeta
        title={t('profile.title')}
        description={t('profile.description')}
      />
      <PageBreadcrumb
        items={[
          { label: t('profile.breadcrumb') }
        ]}
        pageTitle={t('profile.breadcrumb')}
        actions={
          <Button
            variant="primary"
            size="md"
            onClick={openEditModal}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z"
              />
              <path
                fillRule="evenodd"
                d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                clipRule="evenodd"
              />
            </svg>
            <span>{t('profile.edit')}</span>
          </Button>
        }
      />

      <div className="flex flex-col w-full">
        <div className="grid grid-cols-1 gap-4 md:gap-6 2xl:gap-8">
          <div className="rounded-2xl border border-border-primary bg-bg-card p-4 md:p-6 xl:p-7.5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-4">
                <ProfileField
                  label={t('profile.fields.username')}
                  value={userProfile?.username}
                />
                <ProfileField
                  label={t('profile.fields.email')}
                  value={employee?.email}
                />
                <ProfileField
                  label={t('profile.fields.phone')}
                  value={employee?.phone || '—'}
                />
              </div>

              <div className="flex flex-col gap-4">
                <ProfileField
                  label={t('profile.fields.first_name')}
                  value={userProfile?.first_name || '—'}
                />
                <ProfileField
                  label={t('profile.fields.last_name')}
                  value={userProfile?.last_name || '—'}
                />
                <ProfileField
                  label={t('profile.fields.role')}
                  value={role?.role_name}
                />
                {employee?.telegram_username && (
                  <ProfileField
                    label={t('profile.fields.telegram')}
                    value={
                      <a
                        href={`https://t.me/${employee.telegram_username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-600"
                      >
                        @{employee.telegram_username}
                      </a>
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isEditOpen}
        onClose={closeEditModal}
        maxWidth="800px"
        className="p-0"
      >
        <EditProfileForm
          ref={formRef}
          onSubmit={() => {
            queryClient.invalidateQueries({ queryKey: ['userProfile'] });
            queryClient.invalidateQueries({ queryKey: ['employeeProfile'] });
            originalCloseEditModal();
          }}
          onClose={originalCloseEditModal}
        />
      </Modal>
    </>
  );
};

export default ProfilePage;
